import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { categories, getComicsByCategory } from '@/mocks/comics';
import type { ComicCategory } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'rating'>('popular');

  useEffect(() => {
    if (slug) setActiveTab(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeTab) as ComicCategory | undefined;
  const categoryName = currentCategory?.name || '全部';
  const categoryDesc = currentCategory?.description || '浏览所有韩国漫画作品';

  let comics = getComicsByCategory(activeTab);
  if (sortBy === 'newest') {
    comics = [...comics].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  } else if (sortBy === 'rating') {
    comics = [...comics].sort((a, b) => b.rating - a.rating);
  } else {
    comics = [...comics].sort((a, b) => b.views - a.views);
  }

  useEffect(() => {
    document.title = `${categoryName}韩漫 - 香香漫画韩国漫画免费阅读`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', `香香漫画${categoryName}分类，${categoryDesc}。提供韩国漫画免费阅读和下拉式韩漫大全，内容优质，浏览流畅。`);
    }
  }, [categoryName, categoryDesc]);

  return (
    <>
      <Header />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
          {/* Breadcrumb */}
          <div className="py-4">
            <Breadcrumb
              items={[
                { label: categoryName },
              ]}
            />
          </div>

          {/* Category Header */}
          <div className="py-6 md:py-8">
            <div className="flex items-center gap-3 mb-2">
              {currentCategory && (
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className={`${currentCategory.icon} text-xl text-primary-600`}></i>
                </div>
              )}
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {categoryName}韩漫
              </h1>
            </div>
            <p className="text-sm text-foreground-500 ml-[52px]">{categoryDesc}</p>
          </div>

          {/* Tabs */}
          <div className="border-b border-background-200/70 mb-6">
            <div className="flex items-center flex-wrap gap-1 pb-1 overflow-x-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab(cat.slug);
                    window.history.pushState({}, '', `/category/${cat.slug}`);
                    window.scrollTo({ top: 0, behavior: 'instant' });
                  }}
                  className={`px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Sort & Count */}
          <div className="flex items-center justify-between mb-6">
            <span className="text-sm text-foreground-500">共 <strong className="text-foreground-900">{comics.length}</strong> 部作品</span>
            <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
              {[
                { key: 'popular' as const, label: '人气' },
                { key: 'newest' as const, label: '最新' },
                { key: 'rating' as const, label: '评分' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                    sortBy === opt.key
                      ? 'bg-background-50 text-foreground-900 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Comics Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5 pb-14">
            {comics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}